import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import PostJobForm from 'Douleutaras/views/job/post-job/PostJobForm';
import Modal from 'Douleutaras/components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { SITE_ID } from 'Douleutaras/constants/localization';
import logoYourHero from 'Assets/layout/job/icon-logo-yourhero-round.svg';
import logoDouleutaras from 'Assets/layout/job/icon-logo-douleutaras-round.svg';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { categoryFormSelectors } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { PostJobFormRef, PostJobSteps } from 'Douleutaras/views/job/post-job/hooks/post-job';
import { useSearchParams } from 'react-router-dom';
import Progressbar from 'Douleutaras/components/Progressbar/Progressbar';
import {
  DecidedInitialValues,
  decideInitialValues,
  getCategoryId
} from 'Douleutaras/views/job/post-job/helpers/post-job-helpers';
import { fetchPostingFlow } from 'Douleutaras/redux/data/pros/postingFlowSlice';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';

import './PostJobModal.scss';
import JobSideInfo from './JobSideInfo';
import PostJobModalFooter from './PostJobModalFooter';
import { useJobModalLifecycle } from './useJobModalLifecycle';
import JobFormExitIntent from './exit-intent/JobFormExitIntent';
import { useExitIntent } from './exit-intent/useExitIntent';

interface PostJobModalProps {
  autoDealIds?: Array<string>;
  initialCategoryId?: string;
  closeButtonEnabled?: boolean;
  // Opt-in per surface (#5622): pressing × shows the retention panel instead of
  // closing. Off by default — rebooking and the surfaces with no × of their own
  // have no exit-intent requirement.
  exitIntentEnabled?: boolean;
  internalFlow?: POST_JOB_FLOW;
  iframe?: boolean;
  jumpTo?: PostJobSteps;
  proId?: string;
  proUsername?: string;
  proMugshot?: string;
  sideInfoEnabled?: boolean;
  categoryIconEnabled?: boolean;
  showProAvatar?: boolean;
  backButtonCallback?(): void;
  closeButtonCallback?(): void;
}

export interface PostJobModalRef {
  /**
   * Answer a close request that originated outside React — on the BSDIR surface the
   * × lives on the Django page, not in this tree (#5622).
   *
   * @returns `true` when the request was intercepted and the modal must stay open,
   *          `false` when the caller should go ahead and close.
   */
  requestClose(): boolean;
}

export const PostJobModal = forwardRef<PostJobModalRef, PostJobModalProps>(({
                               autoDealIds,
                               backButtonCallback,
                               categoryIconEnabled = true,
                               closeButtonCallback,
                               closeButtonEnabled = true,
                               exitIntentEnabled = false,
                               iframe = false,
                               initialCategoryId,
                               internalFlow,
                               jumpTo,
                               proId,
                               proUsername,
                               proMugshot,
                               sideInfoEnabled = false,
                               showProAvatar = false
},                                                                          ref) => {
  const { t } = useTranslation();
  const { siteId } = useLocalization();
  const postJobRef = useRef<PostJobFormRef>(undefined);

  // Exit intent replaces the form's body while leaving the form itself mounted,
  // so "Continue with the job" returns everything the customer had already filled in.
  const exitIntent = useExitIntent(exitIntentEnabled);
  const [ nextButtonEnabled, setNextButtonEnabled ] = useState<boolean>();
  const [ backButtonEnabled, setBackButtonEnabled ] = useState<boolean>(false);
  const [ nextButtonText, setNextButtonText ] = useState<string | React.ReactElement>();
  // Set while the location step shows its inline map picker — swaps the action button for Confirm.
  const [ locationMapView, setLocationMapView ] = useState<{ confirmDisabled: boolean }>();
  const [ prosAvailableOverride, setProsAvailableOverride ] = useState<number>();
  const [ isFormValid, setIsFormValid ] = useState<boolean>();
  const [ isFormSubmitting, setIsFormSubmitting ] = useState<boolean>(false);
  const [ searchParams ] = useSearchParams();
  const [ progressTranslateX, setProgressTranslateX ] = useState<string>('-100%');
  const defaultLogo = siteId === SITE_ID.IE ? logoYourHero : logoDouleutaras;
  const dispatch = useAppDispatch();
  const categoryId = getCategoryId(initialCategoryId, searchParams, postJobRef.current?.categoryId);
  const categoryForm = useAppSelector(state => categoryFormSelectors.selectById(state, categoryId));
  const postingFlow = useAppSelector(state => state.pros.postingFlow);

  useJobModalLifecycle();

  const shouldFetchPostingFlow = Boolean(categoryId);

  const stepChangeCallback = useCallback(questions => {
    // postingFlow API call disabled if no sideInfo is enabled (rebooking) or category is missing
    if (!shouldFetchPostingFlow) {
      return;
    }

    // Enable fetching additional info if sideInfoEnabled is enabled
    dispatch(fetchPostingFlow({ categoryId, questions }));
  }, [shouldFetchPostingFlow, dispatch, categoryId]);

  const progressBarUpdate = useCallback(payload => {
    const translate = payload?.progressTranslateX;

    if (typeof translate === 'string') {
      setProgressTranslateX(translate);
    }

  }, []);

  const initialValues: DecidedInitialValues = decideInitialValues(searchParams, categoryId);
  const postJobFlow = internalFlow === POST_JOB_FLOW.DIRECT_CONTACT
    ? POST_JOB_FLOW.DIRECT_CONTACT
    : POST_JOB_FLOW.LEGACY;
  const nextButtonDisabled = !isFormValid || isFormSubmitting || (shouldFetchPostingFlow && !postingFlow.loaded);
  const canCloseModal = closeButtonEnabled && typeof closeButtonCallback === 'function';

  // The × means three different things, and the order is the priority: the map
  // picker owns it first, then exit intent, and only then does it close the modal.
  //
  // It answers whether the request was swallowed, because the BSDIR surface asks
  // this same question from outside React — see `PostJobModalRef.requestClose`.
  const requestClose = useCallback((): boolean => {
    if (locationMapView) {
      postJobRef.current?.closeLocationMap();

      return true;
    }

    // The panel has had its say: a second × closes the form for real.
    if (!exitIntentEnabled || exitIntent.isOpen) {
      return false;
    }

    exitIntent.open();

    return true;
  }, [locationMapView, exitIntentEnabled, exitIntent.isOpen, exitIntent.open]);

  // The only way into `requestClose` for a surface whose × is rendered by Django.
  useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

  const resolveCloseButtonCallback = () => {
    if (locationMapView) {
      return () => postJobRef.current.closeLocationMap();
    }

    if (!canCloseModal) {
      return undefined;
    }

    return () => {
      if (!requestClose()) {
        closeButtonCallback();
      }
    };
  };

  const handleBack = () => {
    // Stepping the hidden form backwards would look like nothing happened,
    // and silently lose a step. Back means "out of this panel".
    if (exitIntent.isOpen) {
      exitIntent.close();
    } else if (typeof backButtonCallback === 'function') {
      backButtonCallback();
    } else if (typeof postJobRef?.current?.questionsRef?.current?.switchStepBack === 'function') {
      postJobRef.current.questionsRef.current.switchStepBack();
    } else {
      postJobRef.current.stepBack();
    }
  };

  const handleNext = () => {
    if (typeof postJobRef?.current?.questionsRef?.current?.switchToNextStep === 'function') {
      postJobRef.current.questionsRef.current.switchToNextStep();
    } else {
      postJobRef.current.goToNextStep();
    }
  };

  return <Modal isOpen dismissCallback={() => true} iframe={iframe}>
    <DefaultModal
      sideInfo={
          sideInfoEnabled &&
            <>
              <JobSideInfo categoryId={postJobRef.current?.categoryId || categoryId}
                           prosAvailableOverride={prosAvailableOverride}
              />
              <div styleName="progress">
                <Progressbar value={progressTranslateX} />
              </div>
            </>
      }
      userMugshot={showProAvatar ? (proMugshot || defaultLogo) : undefined}
      serviceIcon={categoryIconEnabled ? categoryForm?.category?.category_group.image : undefined}
      showBackButton={backButtonEnabled && !locationMapView}
      fullWidth={Boolean(locationMapView)}
      backText={t('T.POST-JOB.Go Back')}
      fullHeight={exitIntent.isOpen}
      closeButtonCallback={resolveCloseButtonCallback()}
      backButtonCallback={handleBack}
      footer={
        exitIntent.isOpen
          // The retention panel's two buttons sit in the body, per the designs, so
          // the footer bar (and its 86px min-height) must go entirely.
          ? undefined
          : <PostJobModalFooter locationMapView={locationMapView}
                                nextButtonEnabled={nextButtonEnabled}
                                nextButtonText={nextButtonText}
                                nextButtonDisabled={nextButtonDisabled}
                                onNext={handleNext}
                                onConfirmMap={() => postJobRef.current.confirmLocationMap()}
            />
      }
    >
      { /* `hidden`, not unmounted: the form keeps its step, its history and every
           answer, which is what "Continue with the job" restores. It also drops
           out of the tab order and the accessibility tree while covered. */ }
      <div hidden={exitIntent.isOpen}>
        <PostJobForm
          iframe={iframe}
          autoDealIds={autoDealIds}
          categoryId={categoryId}
          ref={postJobRef}
          setNextButtonEnabled={setNextButtonEnabled}
          setNextButtonText={setNextButtonText}
          setBackButtonEnabled={setBackButtonEnabled}
          setLocationMapView={setLocationMapView}
          setProsAvailableOverride={setProsAvailableOverride}
          setIsFormValid={setIsFormValid}
          setIsFormSubmitting={setIsFormSubmitting}
          stepChangeCallback={stepChangeCallback}
          progressBarUpdate={progressBarUpdate}
          isFormValid={isFormValid}
          isFormSubmitting={isFormSubmitting}
          initialValues={initialValues.form}
          initialQuestionsHistory={initialValues.initialQuestionsHistory}
          jumpTo={jumpTo ?? initialValues.lastStoredStep}
          proId={proId}
          proUsername={proUsername}
          postJobFlow={postJobFlow}
        />
      </div>

      { exitIntent.isOpen &&
        <JobFormExitIntent onContinue={exitIntent.close}
                           onCancel={() => closeButtonCallback?.()}
        />
      }
    </DefaultModal>
  </Modal>;
});

PostJobModal.displayName = 'PostJobModal';

export default PostJobModal;
